import React, { useRef } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { Upload, X, Image as ImageIcon } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { backendUrl, uploadImage } from '../../lib/api';

const ImageUpload = ({ value, onChange, label }) => {
  const { t } = useLanguage();
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = React.useState(false);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error(t('نوع الملف غير مدعوم', 'File type not supported'));
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error(t('حجم الملف كبير جداً (الحد الأقصى 5 ميجابايت)', 'File too large (max 5MB)'));
      return;
    }

    setUploading(true);
    try {
      const response = await uploadImage(file);
      const imageUrl = backendUrl(response.data.url);
      onChange(imageUrl);
      toast.success(t('تم رفع الصورة بنجاح', 'Image uploaded successfully'));
    } catch (error) {
      toast.error(t('فشل رفع الصورة', 'Failed to upload image'));
    } finally {
      setUploading(false);
    }
  };

  const handleUrlChange = (e) => {
    onChange(e.target.value);
  };

  const handleRemove = () => {
    onChange('');
  };

  return (
    <div className="space-y-3">
      {label && <span className="text-gray-300 text-sm">{label}</span>}
      
      {/* Preview */}
      {value && (
        <div className="relative inline-block">
          <div className="w-full max-w-xs h-32 rounded-lg overflow-hidden border border-white/10">
            <img src={value} alt="Preview" className="w-full h-full object-cover" />
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={handleRemove}
            className="absolute top-2 end-2 bg-red-500/80 hover:bg-red-500 text-white w-6 h-6"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      )}

      {/* Upload Options */}
      <div className="flex flex-col sm:flex-row gap-3">
        {/* File Upload Button */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/gif,image/webp"
          onChange={handleFileSelect}
          className="hidden"
        />
        <Button
          type="button"
          variant="outline"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="border-white/20 text-white hover:bg-white/10"
        >
          {uploading ? (
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin me-2" />
          ) : (
            <Upload className="w-4 h-4 me-2" />
          )}
          {t('رفع صورة', 'Upload Image')}
        </Button>

        <span className="text-gray-500 self-center text-sm">{t('أو', 'or')}</span>

        {/* URL Input */}
        <div className="flex-1">
          <Input
            value={value}
            onChange={handleUrlChange}
            placeholder={t('رابط الصورة', 'Image URL')}
            className="bg-white/5 border-white/10 text-white"
          />
        </div>
      </div>

      {!value && (
        <div className="flex items-center gap-2 text-gray-500 text-xs">
          <ImageIcon className="w-4 h-4" />
          {t('JPEG, PNG, GIF, WebP (الحد الأقصى 5 ميجابايت)', 'JPEG, PNG, GIF, WebP (max 5MB)')}
        </div>
      )}
    </div>
  );
};

export default ImageUpload;
